import {
    PurchasingContainer,
    PurchasingRegister,
} from '@Purchasing/Data/DataSource/Container'
import {
    PurchaseProcess,
    SupplierQuote,
    SupplierQuoteStatus,
} from '@Purchasing/Domain/Model/PurchaseProcess'
import {
    CreateSupplierQuotePayload,
    UpdateSupplierQuotePayload,
} from '@Purchasing/Data/DataSource/API/Entity/PurchaseProcess'
import { useCallback, useState } from 'react'

export const useSupplierQuoteActions = () => {
    const createUseCase = PurchasingContainer.get(
        PurchasingRegister.UseCaseCreateSupplierQuote
    )
    const updateUseCase = PurchasingContainer.get(
        PurchasingRegister.UseCaseUpdateSupplierQuote
    )
    const statusUseCase = PurchasingContainer.get(
        PurchasingRegister.UseCaseUpdateSupplierQuoteStatus
    )
    const selectUseCase = PurchasingContainer.get(
        PurchasingRegister.UseCaseSelectSupplierQuote
    )
    const deleteUseCase = PurchasingContainer.get(
        PurchasingRegister.UseCaseDeleteSupplierQuote
    )

    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    const runAction = useCallback(
        async <T>(action: () => Promise<T>, fallbackError: string) => {
            setLoading(true)
            setError(null)
            try {
                return await action()
            } catch (e: any) {
                console.error(e)
                setError(e?.response?.data?.message ?? fallbackError)
                return null
            } finally {
                setLoading(false)
            }
        },
        []
    )

    const createQuote = useCallback(
        (processId: string, payload: CreateSupplierQuotePayload) =>
            runAction<SupplierQuote>(
                () => createUseCase.execute(processId, payload),
                'Erro ao criar a cotação'
            ),
        [createUseCase, runAction]
    )

    const updateQuote = useCallback(
        (
            processId: string,
            quoteId: string,
            payload: UpdateSupplierQuotePayload
        ) =>
            runAction<SupplierQuote>(
                () => updateUseCase.execute(processId, quoteId, payload),
                'Erro ao atualizar a cotação'
            ),
        [updateUseCase, runAction]
    )

    const changeStatus = useCallback(
        (processId: string, quoteId: string, status: SupplierQuoteStatus) =>
            runAction<SupplierQuote>(
                () => statusUseCase.execute(processId, quoteId, status),
                'Erro ao alterar o status da cotação'
            ),
        [statusUseCase, runAction]
    )

    const selectWinner = useCallback(
        (processId: string, quoteId: string) =>
            runAction<PurchaseProcess>(
                () => selectUseCase.execute(processId, quoteId),
                'Erro ao selecionar a cotação'
            ),
        [selectUseCase, runAction]
    )

    const removeQuote = useCallback(
        (processId: string, quoteId: string) =>
            runAction<void>(
                () => deleteUseCase.execute(processId, quoteId),
                'Erro ao remover a cotação'
            ),
        [deleteUseCase, runAction]
    )

    return {
        createQuote,
        updateQuote,
        changeStatus,
        selectWinner,
        removeQuote,
        loading,
        error,
    }
}
